import { useEffect, useState } from 'react';
import { singletonHook } from 'react-singleton-hook';
import { signInWithCustomToken, User } from 'firebase/auth';
import { AmplifyGet } from '@sunrun/sales-experience-shared/amplify-api-wrapper';
import { firebaseAuth } from '../lib/firebase';
import { logger } from './logger';
import { useSubscribe, publish } from 'helpers/messaging';
import type { Message } from 'helpers/messaging';

const hybridTokenCache = '';
const hybridTokenTypeCache = '';

const useHybridToken = singletonHook(
  {
    hybridToken: '',
    hybridTokenType: '',
  },
  () => {
    const [hybridToken, setHybridToken] = useState(hybridTokenCache);
    const [hybridTokenType, setHybridTokenType] =
      useState(hybridTokenTypeCache);

    if (!hybridToken) {
      publish.queryAuthToken();
    }
    useSubscribe((message: Message) => {
      if (message.type === 'EVENT:AUTH_TOKEN') {
        setHybridToken(message.payload.token);
        setHybridTokenType(message.payload.tokenType);
      }
    });

    return {
      hybridToken,
      hybridTokenType,
    };
  },
);

const useFirebaseUser = singletonHook(
  {
    loadingFirebaseUser: false,
    errorFirebaseUser: null,
    firebaseUser: null,
  },
  () => {
    const { hybridToken } = useHybridToken();
    const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
    const [loadingFirebaseUser, setLoadingFirebaseUser] = useState(false);
    const [errorFirebaseUser, setErrorFirebaseUser] = useState<Error | null>(
      null,
    );
    useEffect(() => {
      // If no HT auth token is provided, keep loading state true and return null for user
      if (!hybridToken) {
        setLoadingFirebaseUser(true);
        setFirebaseUser(null);
        return;
      }

      // Reset states
      setLoadingFirebaseUser(true);
      setErrorFirebaseUser(null);

      const exchangeTokenAndSignIn = async () => {
        try {
          const data = await AmplifyGet<{ firebaseToken: string }>(
            'Exchange Firebase Token',
            'SalesExpDashboardApi',
            `/exchangeFirebaseToken`,
            {
              headers: {
                Authorization: `Bearer ${hybridToken}`,
              },
            },
            logger,
            {
              expectedErrorCodes: [401, 403],
            },
          );

          if (!data.firebaseToken) {
            throw new Error('Failed to exchange token');
          }

          // Sign in with the custom Firebase token
          const userCredential = await signInWithCustomToken(
            firebaseAuth,
            data.firebaseToken,
          );
          setFirebaseUser(userCredential.user);
        } catch (err) {
          console.error('Error signing in with Firebase:', err);
          setErrorFirebaseUser(
            err instanceof Error
              ? err
              : new Error('Failed to sign in with Firebase'),
          );
        } finally {
          setLoadingFirebaseUser(false);
        }
      };

      exchangeTokenAndSignIn();
    }, [hybridToken]);

    return {
      loadingFirebaseUser,
      errorFirebaseUser,
      firebaseUser,
    };
  },
);

export { useHybridToken, useFirebaseUser };
